import { motion, AnimatePresence } from 'framer-motion';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

import type { Image } from '../../services/api';
import api from '../../services/api';

import FullscreenImage from './FullscreenImage';
import styles from './ImageGallery.module.css';

/**
 * ImageGallery Props
 */
type ImageGalleryProps = {
  readonly categoryId: string;
  readonly radius?: number; // Distance from center for image positioning
  readonly autoRotate?: boolean; // Auto-rotate gallery
  readonly autoRotateInterval?: number; // Interval in ms for auto-rotation
  readonly variant?: 'gallery' | 'shop'; // Display variant (shop shows prices)
  readonly type?: 'portfolio' | 'shop'; // Category type for filtering
};

/**
 * ImageGallery Component
 *
 * Displays images in an infinite 3D carousel layout.
 * Features:
 * - Elliptical positioning with 3D depth effect
 * - Images appear to emerge from the distance at the top
 * - Featured image at bottom is largest and most prominent
 * - Infinite rotation through all images (no pagination)
 * - Click to view fullscreen
 * - Smooth animations with Framer Motion
 * @param root0
 * @param root0.categoryId
 * @param root0.radius
 * @param root0.autoRotate
 * @param root0.autoRotateInterval
 * @param root0.variant
 * @param root0.type
 */
const ImageGallery: React.FC<ImageGalleryProps> = ({
  categoryId,
  radius = 350,
  autoRotate = false,
  autoRotateInterval = 3000,
  variant = 'gallery',
  type,
}) => {
  // State
  const [images, setImages] = useState<ReadonlyArray<Image>>([]);
  const [currentIndex, setCurrentIndex] = useState(0); // Current featured image index
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

  // Number of visible images in the carousel at once
  // More images visible = smoother infinite scroll illusion
  const visibleCount = 10;

  /**
   * Fetch images by category from API
   */
  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        setError(null);
        // Reset to first image when category/type changes
        setCurrentIndex(0);
        const response = await api.images.getImages(categoryId, 1, 100, type);
        const sortedImages = [...response.images].sort((a, b) => a.order - b.order);
        setImages(sortedImages);
      } catch (err) {
        console.error('Error fetching images:', err);
        setError(err instanceof Error ? err.message : 'Failed to load images');
      } finally {
        setLoading(false);
      }
    };

    if (categoryId) {
      fetchImages();
    }
  }, [categoryId, type]);

  /**
   * Get visible images for the carousel
   * Returns images arranged so that:
   * - Slot 0 (bottom/front) = currentIndex (featured image)
   * - Slots 1,2,3... going clockwise (right side, then top)
   * - Slots ...,n-2,n-1 going counter-clockwise (left side)
   * This ensures new images "spawn" at the top (back) when rotating
   */
  const visibleImages = useMemo(() => {
    if (images.length === 0) return [];
    if (images.length < visibleCount) {
      // If we have fewer images than visible slots, show all
      return images.map((img, idx) => ({ image: img, originalIndex: idx }));
    }

    // Arrange images so slot 0 = currentIndex (bottom/front)
    // Going clockwise: slot 1 = currentIndex+1, slot 2 = currentIndex+2, etc.
    // Going counter-clockwise: slot n-1 = currentIndex-1, slot n-2 = currentIndex-2, etc.
    // The "back" of the carousel (top) is where new images should appear

    // Use mutable array for building, return as readonly
    const result: Array<{ image: Image; originalIndex: number }> = [];

    // Slot 0 = featured image (currentIndex)
    result.push({ image: images[currentIndex], originalIndex: currentIndex });

    // Fill slots going around the circle
    // Half go clockwise (positive direction), half go counter-clockwise (negative direction)
    const halfSlots = Math.floor((visibleCount - 1) / 2);

    // Clockwise slots (1, 2, 3, ... up to halfSlots) - these go up the right side toward the back
    for (let i = 1; i <= halfSlots; i++) {
      const idx = (currentIndex + i) % images.length;
      result.push({ image: images[idx], originalIndex: idx });
    }

    // Counter-clockwise slots (n-1, n-2, ... ) - these go up the left side toward the back
    for (let i = 1; i <= visibleCount - 1 - halfSlots; i++) {
      const idx = (currentIndex - i + images.length) % images.length;
      result.push({ image: images[idx], originalIndex: idx });
    }

    return result as ReadonlyArray<{ readonly image: Image; readonly originalIndex: number }>;
  }, [images, currentIndex, visibleCount]);

  /**
   * Rotate to next image
   */
  const handleNext = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  /**
   * Rotate to previous image
   */
  const handlePrevious = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  /**
   * Auto-rotate logic
   */
  useEffect(() => {
    if (!autoRotate || images.length === 0) return;

    const interval = setInterval(() => {
      handleNext();
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [autoRotate, autoRotateInterval, images.length, handleNext]);

  /**
   * Calculate position for image in the 3D carousel
   * Creates the illusion of images orbiting around the menu circle:
   * - Slot 0 = bottom center (featured, front)
   * - Slots 1,2,3,4 = right side going up to top (clockwise, future images)
   * - Slots 5,6,7,8,9 = left side going up from bottom to top (counter-clockwise, past images)
   * - Images at top appear to be "behind" the menu
   */
  const calculatePosition = useCallback(
    (slotIndex: number, totalSlots: number) => {
      if (totalSlots === 0) return { x: 0, y: 0, scale: 1, opacity: 1, blur: 0, zIndex: 1 };

      // Single image case - position below menu
      if (totalSlots === 1) {
        return { x: 0, y: 260, scale: 1.3, opacity: 1, blur: 0, zIndex: 100, isFeatured: true };
      }

      const isFeatured = slotIndex === 0;
      const halfSlots = Math.floor((totalSlots - 1) / 2);

      // Calculate angle based on slot position
      // Using standard math coordinates: angle 0 = right, PI/2 = up, PI = left, -PI/2 = down
      // But we want: bottom = front, top = back
      // So we use: PI/2 = bottom (front), -PI/2 = top (back)
      let angle: number;

      if (slotIndex === 0) {
        // Featured image at bottom center
        angle = Math.PI / 2; // Bottom (6 o'clock)
      } else if (slotIndex <= halfSlots) {
        // Right side: slots 1,2,3,4 go from bottom-right up toward top-right
        // These are the "next" images (clockwise direction)
        const progress = slotIndex / (halfSlots + 1);
        // Go from just right of bottom (angle slightly less than PI/2) to near top (angle near -PI/2)
        angle = Math.PI / 2 - progress * Math.PI; // PI/2 -> -PI/2 (bottom -> top, right side)
      } else {
        // Left side: slots (halfSlots+1) to (totalSlots-1) go from bottom-left up toward top-left
        // These are the "previous" images (counter-clockwise direction)
        const leftSlotIndex = slotIndex - halfSlots; // 1, 2, 3, ...
        const leftSlotCount = totalSlots - 1 - halfSlots;
        const progress = leftSlotIndex / (leftSlotCount + 1);
        // Go from just left of bottom (angle slightly more than PI/2) to near top (angle near PI + PI/2)
        // Use negative x by going from PI/2 toward PI and beyond to 3PI/2
        angle = Math.PI / 2 + progress * Math.PI; // PI/2 -> 3PI/2 (bottom -> top, left side)
      }

      // Elliptical orbit - wider than tall to create 3D perspective
      const radiusX = radius * 1.15; // Horizontal spread
      const radiusY = radius * 0.6; // Vertical spread (elongated for more vertical motion)

      // Calculate position on ellipse
      const x = Math.cos(angle) * radiusX;

      // Y position: shift ellipse center down so bottom image is lower
      const ellipseCenterY = 60; // Positive = lower (increased for more separation)
      const y = Math.sin(angle) * radiusY + ellipseCenterY;

      // Depth calculation: bottom = front (1), top = back (0)
      // sin(PI/2) = 1 (bottom/front), sin(-PI/2) = sin(3PI/2) = -1 (top/back)
      const normalizedDepth = (Math.sin(angle) + 1) / 2;

      // 3D DEPTH EFFECT
      // Scale: tiny at back (0.12), large at front (1.1)
      const baseScale = 0.12 + normalizedDepth * 0.98;
      const scale = isFeatured ? 1.5 : baseScale;

      // Opacity: faded at back (0.15), full at front (1.0)
      const baseOpacity = 0.15 + normalizedDepth * 0.85;
      const opacity = isFeatured ? 1.0 : baseOpacity;

      // Blur: blurred at back (4px), clear at front (0px)
      const blur = isFeatured ? 0 : (1 - normalizedDepth) * 4;

      // Z-index: back images behind menu, front images in front
      const zIndex = isFeatured ? 150 : Math.round(normalizedDepth * 100);

      return { x, y, scale, opacity, blur, zIndex, isFeatured };
    },
    [radius]
  );

  /**
   * Open fullscreen view
   */
  const openFullscreen = useCallback((index: number) => {
    setFullscreenIndex(index);
  }, []);

  /**
   * Close fullscreen view
   */
  const closeFullscreen = useCallback(() => {
    setFullscreenIndex(null);
  }, []);

  /**
   * Navigate in fullscreen mode
   */
  const fullscreenNext = useCallback(() => {
    if (fullscreenIndex !== null) {
      setFullscreenIndex((prev) => (prev! + 1) % images.length);
    }
  }, [fullscreenIndex, images.length]);

  const fullscreenPrevious = useCallback(() => {
    if (fullscreenIndex !== null) {
      setFullscreenIndex((prev) => (prev! - 1 + images.length) % images.length);
    }
  }, [fullscreenIndex, images.length]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.gallery}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading images...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.gallery}>
        <div className={styles.error}>
          <p>Failed to load images</p>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (images.length === 0) {
    return (
      <div className={styles.gallery}>
        <div className={styles.empty}>
          <p>No images in this category</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.gallery}>
        {/* Images Container */}
        <div className={styles.imagesContainer}>
          <AnimatePresence mode="popLayout">
            {visibleImages.map(({ image, originalIndex }, slotIndex) => {
              const position = calculatePosition(slotIndex, visibleImages.length);
              const isFeatured = position.isFeatured ?? false;

              // Spawn position: at top of ellipse trajectory (matches the oval path)
              const spawnY = -radius * 0.6 + 60; // Top of ellipse (uses same radiusY and ellipseCenterY)

              return (
                <motion.div
                  key={image._id}
                  className={`${styles.imageWrapper} ${isFeatured ? styles.featured : ''}`}
                  initial={{ opacity: 0, scale: 0.05, y: spawnY, x: 0 }}
                  animate={{
                    x: position.x,
                    y: position.y,
                    scale: position.scale,
                    opacity: position.opacity,
                    filter: `blur(${position.blur}px)`,
                    zIndex: position.zIndex,
                  }}
                  exit={{ opacity: 0, scale: 0.05, y: spawnY, x: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 120,
                    damping: 20,
                    mass: 0.8,
                  }}
                  onClick={() => openFullscreen(originalIndex)}
                  style={{ zIndex: position.zIndex }}
                >
                  <img
                    src={image.mediumUrl ?? image.url}
                    srcSet={
                      (image.thumbnailUrl ?? image.mediumUrl ?? image.url) !== ''
                        ? `${image.thumbnailUrl} 300w, ${image.mediumUrl} 1000w, ${image.url} 1920w`
                        : undefined
                    }
                    sizes="(max-width: 480px) 180px, (max-width: 768px) 240px, 360px"
                    alt={image.title ?? image.originalName}
                    className={styles.image}
                    loading="lazy"
                    style={{ imageRendering: 'crisp-edges' }}
                  />
                  {isFeatured && image.title ? (
                    <div className={styles.imageTitle}>{image.title}</div>
                  ) : null}
                  {isFeatured && variant === 'shop' && image.price !== undefined ? (
                    <div className={styles.priceBadge}>${image.price.toFixed(2)}</div>
                  ) : null}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Carousel Controls (Bottom) */}
        {images.length > 1 && (
          <div className={styles.controls}>
            <button
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={handlePrevious}
              aria-label="Previous image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div className={styles.indicator}>
              <span className={styles.currentPage}>{currentIndex + 1}</span>
              <span className={styles.pageSeparator}>/</span>
              <span className={styles.totalPages}>{images.length}</span>
            </div>

            <button
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={handleNext}
              aria-label="Next image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Fullscreen Viewer */}
      <AnimatePresence>
        {fullscreenIndex !== null && (
          <FullscreenImage
            image={images[fullscreenIndex]}
            isOpen={true}
            onClose={closeFullscreen}
            onNext={images.length > 1 ? fullscreenNext : undefined}
            onPrevious={images.length > 1 ? fullscreenPrevious : undefined}
            variant={variant}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageGallery;
